import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Globe2, Lock, Users, Check, X, Clock } from "lucide-react";

function NewInquiryDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    category: "",
    target_volume: "",
    unit: "",
    destination_country: "",
    budget: "",
    notes: "",
    is_public: true,
  });
  const [busy, setBusy] = useState(false);

  const reset = () =>
    setF({
      category: "",
      target_volume: "",
      unit: "",
      destination_country: "",
      budget: "",
      notes: "",
      is_public: true,
    });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/inquiries", f);
      toast.success("Bulk inquiry submitted");
      setOpen(false);
      reset();
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-inquiry-btn">+ New bulk inquiry</Button>
      </DialogTrigger>
      <DialogContent data-testid="new-inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Bulk sourcing inquiry</DialogTitle>
          <DialogDescription>
            Post a bulk requirement. Approved Safeway Finland suppliers can bid — you decide who wins.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Category</Label>
            <Input
              data-testid="inq-category"
              value={f.category}
              onChange={(e) => setF({ ...f, category: e.target.value })}
              placeholder="e.g. Robusta coffee beans, Rattan baskets"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Target volume</Label>
              <Input
                data-testid="inq-volume"
                value={f.target_volume}
                onChange={(e) => setF({ ...f, target_volume: e.target.value })}
                placeholder="e.g. 5,000"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Input
                data-testid="inq-unit"
                value={f.unit}
                onChange={(e) => setF({ ...f, unit: e.target.value })}
                placeholder="kg / units / pallets"
              />
            </div>
          </div>
          <div>
            <Label>Destination country</Label>
            <Input
              data-testid="inq-dest"
              value={f.destination_country}
              onChange={(e) => setF({ ...f, destination_country: e.target.value })}
              placeholder="Finland, Germany…"
            />
          </div>
          <div>
            <Label>Budget (optional)</Label>
            <Input
              data-testid="inq-budget"
              value={f.budget}
              onChange={(e) => setF({ ...f, budget: e.target.value })}
              placeholder="e.g. up to €4.20 / kg CIF"
            />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              data-testid="inq-notes"
              rows={4}
              value={f.notes}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
              placeholder="Certifications, packaging, target lead time, quality specs…"
            />
          </div>
          <div
            data-testid="inq-public-row"
            className="flex items-start gap-4 rounded-md border border-border bg-muted/40 p-4"
          >
            <div className="flex-1">
              <div className="font-heading text-sm font-bold text-secondary flex items-center gap-2">
                {f.is_public ? <Globe2 className="w-4 h-4 text-primary" /> : <Lock className="w-4 h-4 text-muted-foreground" />}
                Open to the RFQ Marketplace
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {f.is_public
                  ? "Approved Safeway suppliers can see and bid on this RFQ. Your name and email stay hidden — only your country is shown."
                  : "Only Safeway Finland admins will see this. Keep it private if you have a preferred supplier."}
              </p>
            </div>
            <Switch
              data-testid="inq-public-toggle"
              checked={f.is_public}
              onCheckedChange={(v) => setF({ ...f, is_public: v })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="inq-submit" onClick={submit} disabled={busy || !f.category}>
            {busy ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BidsDialog({ inquiry, open, onOpenChange, onChanged }) {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [decidingId, setDecidingId] = useState(null);

  const load = React.useCallback(() => {
    if (!inquiry) return;
    setLoading(true);
    api
      .get(`/inquiries/${inquiry.id}/bids`)
      .then((r) => setBids(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load bids"))
      .finally(() => setLoading(false));
  }, [inquiry]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const decide = async (bidId, decision) => {
    setDecidingId(bidId);
    try {
      await api.patch(`/rfq/bids/${bidId}`, { decision });
      toast.success(decision === "accepted" ? "Bid accepted — inquiry closed" : "Bid declined");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update bid");
    } finally {
      setDecidingId(null);
    }
  };

  const awarded = inquiry?.status === "awarded";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bids-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            Bids for “{inquiry?.category}”
          </DialogTitle>
          <DialogDescription>
            {awarded
              ? "This inquiry has been awarded. No further bids can be accepted."
              : "Review supplier bids. Accepting one closes the RFQ and auto-declines the others."}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading bids…</div>
        ) : bids.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="bids-empty">
            No bids yet. Suppliers on the marketplace typically respond within 3–7 working days.
          </div>
        ) : (
          <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
            {bids.map((b) => (
              <div key={b.id} className="py-4" data-testid={`bid-row-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading text-sm font-bold text-secondary">
                      {b.supplier?.company_name || b.supplier?.name || "Supplier"}
                      {b.supplier?.country && (
                        <span className="text-muted-foreground font-normal"> · {b.supplier.country}</span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {new Date(b.created_at).toLocaleString()}
                    </div>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground">
                      Price
                    </div>
                    <div className="text-sm text-secondary font-medium">
                      {b.currency} {Number(b.price_per_unit).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground">
                      MOQ
                    </div>
                    <div className="text-sm text-secondary font-medium">{b.moq_units}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground">
                      Lead time
                    </div>
                    <div className="text-sm text-secondary font-medium flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {b.lead_time_days} days
                    </div>
                  </div>
                </div>
                {b.notes && (
                  <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {b.notes}
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    data-testid={`bid-accept-${b.id}`}
                    size="sm"
                    className="h-9 px-4"
                    disabled={awarded || b.status !== "submitted" || decidingId === b.id}
                    onClick={() => decide(b.id, "accepted")}
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" /> Accept bid
                  </Button>
                  <Button
                    data-testid={`bid-reject-${b.id}`}
                    size="sm"
                    variant="outline"
                    className="h-9 px-4"
                    disabled={awarded || b.status !== "submitted" || decidingId === b.id}
                    onClick={() => decide(b.id, "rejected")}
                  >
                    <X className="w-3.5 h-3.5 mr-1.5" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BuyerInquiries() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [bidsOpen, setBidsOpen] = useState(false);

  const load = () =>
    api
      .get("/buyer/inquiries")
      .then((r) => setItems(r.data))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  return (
    <DashboardLayout title="Bulk inquiries" actions={<NewInquiryDialog onCreated={load} />}>
      <Card>
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">Your bulk inquiries</CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm" data-testid="inquiries-empty">
              No inquiries yet. Post one to open it up to the Safeway supplier marketplace.
            </div>
          ) : (
            <div className="divide-y divide-border" data-testid="inquiries-list">
              {items.map((i) => (
                <div key={i.id} className="py-4" data-testid={`inquiry-${i.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-secondary flex items-center gap-2 flex-wrap">
                        {i.category}
                        {i.is_public ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-heading font-black uppercase tracking-widest text-primary bg-primary/10 border border-primary/25 rounded-full px-2 py-0.5">
                            <Globe2 className="w-3 h-3" /> Public
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground bg-muted border border-border rounded-full px-2 py-0.5">
                            <Lock className="w-3 h-3" /> Private
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Volume: {i.target_volume} {i.unit || ""} · Dest: {i.destination_country} · Budget: {i.budget || "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={i.status} />
                      {i.is_public && (
                        <Button
                          data-testid={`view-bids-${i.id}`}
                          size="sm"
                          variant="outline"
                          className="h-9"
                          onClick={() => {
                            setActive(i);
                            setBidsOpen(true);
                          }}
                        >
                          <Users className="w-3.5 h-3.5 mr-1.5" />
                          {i.bid_count || 0} bid{(i.bid_count || 0) === 1 ? "" : "s"}
                        </Button>
                      )}
                    </div>
                  </div>
                  {i.notes && (
                    <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">
                      {i.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <BidsDialog
        inquiry={active}
        open={bidsOpen}
        onOpenChange={(v) => {
          setBidsOpen(v);
          if (!v) setActive(null);
        }}
        onChanged={load}
      />
    </DashboardLayout>
  );
}
